import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { useRouteParams } from '../hooks/useRouteParams';
import { useNavigate } from '../hooks/useNavigate';
import { EntitiesDTO, FOLDER_404_ERROR, ProviderType } from '../DocumentVault.meta';
import { useFilterableYearOption } from '../state/selectors';
import { queryKeys } from './queryKeys';
import { useFidelityTokenId } from '../hooks/useFidelityTokenId';
import { useDocumentVaultContext } from '../context/useDocumentVaultContext';

interface ListEntitiesParams {
  clientId: string | number;
  path: string | null;
  providerType: ProviderType;
  year?: number;
  tokenId?: string;
}

interface CancelablePromise extends Promise<EntitiesDTO> {
  cancel(): void;
}

const listEntities = ({ clientId, path, providerType, year, tokenId }: ListEntitiesParams) => {
  const controller = new AbortController();
  const { signal } = controller;

  const promise = authFetch(
    urlWithParams('/Consumer/DocumentVault/ListObjects', {
      clientId,
      path: path || null,
      providerType,
      year,
      tokenId,
    }),
    {
      signal,
    },
  ) as CancelablePromise;

  promise.cancel = () => controller.abort();

  return promise;
};

interface Params {
  path: string;
  clientId: string;
  providerType: ProviderType;
  filterableByYear: boolean;
}

export const useListEntitiesQuery = ({
  clientId,
  path,
  providerType,
  filterableByYear,
}: Params) => {
  const { value: selectedYear } = useFilterableYearOption();

  const { getCurrentTokenId } = useFidelityTokenId();

  const tokenId = getCurrentTokenId();

  const navigate = useNavigate();

  const { forceYear } = useDocumentVaultContext();

  const year = forceYear || selectedYear;

  return useQuery(
    queryKeys.listDocumentVaultEntities(
      clientId,
      path,
      providerType,
      filterableByYear ? year : null,
      providerType === ProviderType.All ? undefined : tokenId,
    ),

    () =>
      listEntities({
        clientId,
        path: decodeURIComponent(path),
        providerType,
        year: filterableByYear ? year : undefined,
        tokenId: providerType === ProviderType.All ? undefined : tokenId,
      }),

    {
      onError(err) {
        // Navigate to home if folder doesn't exist.
        if (err.message === FOLDER_404_ERROR) {
          navigate({ path: '', providerType: ProviderType.All });
        }
      },

      retry(failureCount, err) {
        // Do not retry for a folder that does not exist
        return !(err.message === FOLDER_404_ERROR || failureCount > 2);
      },

      enabled: path !== undefined,
    },
  );
};
